/**
 * Palette generation utilities for creating color schemes from a single base color
 */

import type { ColorPalette } from './colorPalettes';

/**
 * Convert hex color to HSL values
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL values to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  
  const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);

  const toHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Generate a 10-color palette from a single base color
 * Creates 4 lighter, the base color, and 5 darker variations
 */
export function generatePaletteFromColor(baseColor: string, id: string, name: string, description?: string): ColorPalette {
  const { h, s, l } = hexToHsl(baseColor);
  
  const colors: string[] = [];
  
  // Generate 4 lighter variants (90%, 80%, 70%, 60% lightness progression)
  const lighterSteps = [90, 80, 70, 60];
  for (const targetL of lighterSteps) {
    if (targetL > l) {
      const newS = Math.max(s * 0.7, 20); 
      colors.push(hslToHex(h, newS, targetL));
    } else {
      const variation = h + (Math.random() - 0.5) * 30; 
      const newH = ((variation % 360) + 360) % 360; 
      colors.push(hslToHex(newH, Math.max(s * 0.8, 15), Math.min(l + 15, 95)));
    }
  }
  
  colors.push(baseColor);
  
  const darkerSteps = [40, 30, 25, 20, 15];
  for (const targetL of darkerSteps) {
    if (targetL < l) {
      const newS = Math.min(s * 1.2, 100); 
      colors.push(hslToHex(h, newS, targetL));
    } else {
      const variation = h + (Math.random() - 0.5) * 30; 
      const newH = ((variation % 360) + 360) % 360; 
      colors.push(hslToHex(newH, Math.min(s * 1.1, 90), Math.max(l - 10, 5)));
    }
  }

  // Ensure palette always has a predictable number of colors if needed, e.g., 10
  // The current logic might produce fewer if base color is very light/dark.
  // For simplicity, we'll use the generated colors array as is.
  // The base color is roughly in the middle of the generated `colors` array.
  const baseColorIndexInGenerated = colors.indexOf(baseColor);
  const defaultColorIndex = baseColorIndexInGenerated !== -1 
                             ? baseColorIndexInGenerated 
                             : Math.floor(colors.length / 2);
  
  return {
    id,
    name,
    description: description || `Gradient palette based on ${baseColor}`,
    colors,
    defaultColorIndex,
    isCustom: true, // Generated palettes are often treated as custom or derivatives
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Generate a monochromatic palette (different saturations and lightness of the same hue)
 */
export function generateMonochromaticPalette(baseColor: string): string[] {
  const { h, s, l } = hexToHsl(baseColor);
  
  const colors: string[] = [];
  
  const variations = [
    { s: s * 0.3, l: 85 },
    { s: s * 0.5, l: 70 },
    { s: s * 0.7, l: 60 },
    { s: s * 0.8, l: 50 },
    { s: s, l: l },
    { s: s * 1.1, l: 40 },
    { s: s * 1.2, l: 30 },
    { s: s * 1.3, l: 25 },
    { s: s * 1.4, l: 20 },
    { s: s * 1.5, l: 15 }
  ];
  
  for (const variant of variations) {
    const clampedS = clamp(variant.s, 0, 100);
    const clampedL = clamp(variant.l, 0, 100);
    colors.push(hslToHex(h, clampedS, clampedL));
  }
  
  return colors;
}

/**
 * Generate an analogous color palette (colors next to each other on the color wheel)
 */
export function generateAnalogousPalette(baseColor: string): string[] {
  const { h, s, l } = hexToHsl(baseColor);
  
  const colors: string[] = [];
  
  const hueVariations = [-30, -20, -15, -10, 0, 10, 15, 20, 25, 30];
  
  for (let i = 0; i < hueVariations.length; i++) {
    const newH = ((h + hueVariations[i]) % 360 + 360) % 360;
    const lightnessMod = (i % 3 - 1) * 10;
    const newL = clamp(l + lightnessMod, 15, 85);
    const saturationMod = (i % 2) * 10;
    const newS = clamp(s + saturationMod, 20, 90);
    colors.push(hslToHex(newH, newS, newL));
  }
  
  return colors;
}

/**
 * Generate a complementary palette (base color + its complement + variations)
 */
export function generateComplementaryPalette(baseColor: string): string[] {
  const { h, s, l } = hexToHsl(baseColor);
  
  const colors: string[] = [];
  
  const baseLightness = [l + 20, l + 10, l, l - 10, l - 20];
  for (const lightness of baseLightness) {
    colors.push(hslToHex(h, s, clamp(lightness, 10, 90)));
  }
  
  const compH = (h + 180) % 360;
  const compLightness = [l + 20, l + 10, l, l - 10, l - 20];
  for (const lightness of compLightness) {
    colors.push(hslToHex(compH, s, clamp(lightness, 10, 90)));
  }
  
  return colors;
}

/**
 * Palette generation modes
 */
export type PaletteMode = 'gradient' | 'monochromatic' | 'analogous' | 'complementary';

/**
 * Generate a palette object using the specified mode.
 * For now, only 'gradient' mode returns a full ColorPalette object.
 * Others return string[] and would need to be wrapped if a full ColorPalette object is needed.
 */
export function generatePalette(
    baseColor: string, 
    mode: PaletteMode = 'gradient', 
    id: string = `gen-${mode}-${baseColor.slice(1)}-${Date.now()}`,
    name: string = `${mode.charAt(0).toUpperCase() + mode.slice(1)} from ${baseColor}`,
    description?: string
): ColorPalette | string[] { // Return type updated
  switch (mode) {
    case 'gradient':
      return generatePaletteFromColor(baseColor, id, name, description);
    case 'monochromatic':
      // These currently return string[], not ColorPalette objects.
      // To make them return ColorPalette, they'd need to be wrapped similarly to generatePaletteFromColor
      // including id, name, description, and defaultColorIndex logic.
      return generateMonochromaticPalette(baseColor);
    case 'analogous':
      return generateAnalogousPalette(baseColor);
    case 'complementary':
      return generateComplementaryPalette(baseColor);
    default:
      return generatePaletteFromColor(baseColor, id, name, description);
  }
} 